import type { IBusiness, OnboardingStep } from "@/business/business";
import { isAdminOrOwnerOrManager } from "@/user/utils";
import { ONBOARDING_STEP } from "./constants";

export const SIMPLE_ONBOARDING_PATHS = {
  root: "/onboarding",
  welcome: "/onboarding/welcome",
  company: "/onboarding/company",
  schedule: "/onboarding/schedule",
  referenceData: "/onboarding/reference-data",
  payPlan: "/onboarding/pay-plan",
  submitted: "/onboarding/submitted",
  // Kept so an old link or bookmark still lands somewhere sensible.
  setup: "/onboarding/setup",
  documents: "/onboarding/documents",
} as const;

export const ONBOARDING_PANE = {
  welcome: "welcome",
  company: "company",
  schedule: "schedule",
  payPlan: "pay-plan",
  submitted: "submitted",
  referenceData: "reference-data",
} as const;

export type OnboardingPane =
  (typeof ONBOARDING_PANE)[keyof typeof ONBOARDING_PANE];

export type OnboardingStepPane = Exclude<
  OnboardingPane,
  typeof ONBOARDING_PANE.welcome | typeof ONBOARDING_PANE.submitted
>;

// The steps the progress bar shows. Welcome is the introduction rather than a step, so it
// is not in here: nothing is asked of them on it.
export const ONBOARDING_STEPS: {
  pane: OnboardingStepPane;
  path: string;
  label: string;
}[] = [
  {
    pane: ONBOARDING_PANE.company,
    path: SIMPLE_ONBOARDING_PATHS.company,
    label: "Company",
  },
  {
    pane: ONBOARDING_PANE.schedule,
    path: SIMPLE_ONBOARDING_PATHS.schedule,
    label: "Pay schedule",
  },
  {
    pane: ONBOARDING_PANE.referenceData,
    path: SIMPLE_ONBOARDING_PATHS.referenceData,
    label: "Reference data",
  },
  {
    pane: ONBOARDING_PANE.payPlan,
    path: SIMPLE_ONBOARDING_PATHS.payPlan,
    label: "Pay plan",
  },
];

export const onboardingStepIndex = (pane: OnboardingStepPane | undefined) =>
  ONBOARDING_STEPS.findIndex((step) => step.pane === pane);

export const isSimpleOnboardingRequired = (
  onboardingStep: OnboardingStep | null | undefined,
) => Boolean(onboardingStep) && onboardingStep !== ONBOARDING_STEP.completed;

/**
 * Whether this request should be sent to the post-submission page. Once a customer has
 * said they are finished, that page is the whole of onboarding: it carries the status and
 * the way to send more, so there is no step page left to be stranded on.
 *
 * @param documentsReadyAt when they said they were finished, if they have
 * @param pathname the path being visited
 */
export const requiresSubmittedRedirect = (
  documentsReadyAt: string | null | undefined,
  pathname: string,
) =>
  Boolean(documentsReadyAt) && pathname !== SIMPLE_ONBOARDING_PATHS.submitted;

// The API sends UTC for a business that has not chosen a zone, and Admin reads it the
// same way, so it does not count as an answer.
const hasCompanyAnswers = (business: OnboardingResumeState) =>
  Boolean(business.name?.trim()) &&
  Boolean(business.time_zone_label) &&
  business.time_zone_label !== "UTC";

export type OnboardingResumeState = Partial<
  Pick<
    IBusiness,
    | "name"
    | "time_zone_label"
    | "documents_ready_at"
    | "onboarding_document_counts"
  >
> & {
  current_pay_period_template?: { id?: string } | null;
};

/**
 * Where a returning customer belongs: the first step they have not answered, rather than
 * the top of a flow they are part way through. Someone who has answered nothing still gets
 * the welcome screen, because for them it is not a resume.
 *
 * @param business what the API knows about their progress
 */
export const resumeOnboardingPath = (business: OnboardingResumeState) => {
  if (business.documents_ready_at) return SIMPLE_ONBOARDING_PATHS.submitted;
  if (!hasCompanyAnswers(business)) return SIMPLE_ONBOARDING_PATHS.welcome;
  if (!business.current_pay_period_template?.id) {
    return SIMPLE_ONBOARDING_PATHS.schedule;
  }

  if (!business.onboarding_document_counts?.reference_data) {
    return SIMPLE_ONBOARDING_PATHS.referenceData;
  }

  // Everything else answered, so they are on the last step whether or not a plan is in:
  // that page is where finishing happens.
  return SIMPLE_ONBOARDING_PATHS.payPlan;
};

export const requiresSimpleOnboardingRedirect = (
  onboardingStep: OnboardingStep | null | undefined,
  pathname: string,
) =>
  isSimpleOnboardingRequired(onboardingStep) &&
  !pathname.startsWith(SIMPLE_ONBOARDING_PATHS.root);

/**
 * Whether this user may run simple onboarding, rather than wait for someone who can.
 *
 * Mirrors `User#may_manage_business_setup?` on the API, which is what actually enforces
 * it — this only decides which screen to show. A technician or rep at a setting-up
 * business gets the waiting screen: they cannot rename the company, change its pay
 * schedule, or read its payroll documents.
 */
export const canManageSimpleOnboarding = (role: string | undefined) =>
  isAdminOrOwnerOrManager(role);
